import Cookies from 'js-cookie';
import UserAuthService from '@Helpers/URLHelpers/UserAuthService';

class BillingService {

    constructor() {
        var originalFetch = require('isomorphic-fetch');
        this.fetch = require('fetch-retry')(originalFetch);

        // Plans
        this.BILLING_PLAN = '/api/billing/plan';
        this.PLAN_GET_BY_ID = '/{planId}';
        this.PLAN_COMPARE = '/compare';

        // Subscriptions
        this.BILLING_SUBSCRIPTION = '/api/billing/subscription';
        this.SUBSCRIPTION_GET_BY_ORG_ID = '/org/{orgId}';
        this.SUBSCRIPTION_UPGRADE = '/org/{orgId}/upgrade';
        this.SUBSCRIPTION_CREATE_UPGRADE_ORDER = '/org/{orgId}/upgrade/order';
        this.SUBSCRIPTION_CONFIRM_UPGRADE = '/org/{orgId}/upgrade/confirm';
        this.SUBSCRIPTION_CANCEL = '/org/{orgId}/cancel';
        this.SUBSCRIPTION_REACTIVATE = '/org/{orgId}/reactivate';
        this.SUBSCRIPTION_RESTART = '/org/{orgId}/restart';
        this.SUBSCRIPTION_HISTORY = '/org/{orgId}/history';
        this.SUBSCRIPTION_SCHEDULE_DOWNGRADE = '/org/{orgId}/schedule-downgrade';
        this.SUBSCRIPTION_PRORATION_PREVIEW = '/org/{orgId}/proration';
        this.SUBSCRIPTION_CONFIRM_PAYMENT = '/org/{orgId}/confirm-payment';

        // Usage
        this.BILLING_USAGE = '/api/billing/usage';
        this.USAGE_RECORD_TICKET = '/ticket';
        this.USAGE_RECORD_CHATBOT_RESOLUTION = '/chatbot-resolution';
        this.USAGE_GET_CURRENT = '/org/{orgId}/current';
        this.USAGE_GET_HISTORY = '/org/{orgId}/history';

        // Invoices
        this.BILLING_INVOICE = '/api/billing/invoice';
        this.INVOICE_GET_BY_ORG_ID = '/org/{orgId}';
        this.INVOICE_GET_BY_ID = '/{invoiceId}';
        this.INVOICE_DOWNLOAD = '/{invoiceId}/download';
        this.INVOICE_ENSURE_FOR_MONTH = '/org/{orgId}/month/{billingMonth}/ensure';

        // Addons
        this.BILLING_ADDON = '/api/billing/addon';
        this.ADDON_CREATE_ORDER = '/order';
        this.ADDON_GET_BY_ORG_ID = '/org/{orgId}';

        // Feature Gate
        this.BILLING_FEATURE_GATE = '/api/billing/feature-gate';
        this.FEATURE_GATE_CHECK = '/org/{orgId}/check';
        this.FEATURE_GATE_ALL = '/org/{orgId}/all';

        // Coupons
        this.BILLING_COUPON = '/api/billing/coupon';
        this.COUPON_VALIDATE = '/validate';
        this.COUPON_RESOLVE_PLAN = '/resolve-plan';
        this.COUPON_DEACTIVATE = '/{couponId}/deactivate';

        // Config (public keys)
        this.BILLING_CONFIG = '/api/billing/billing-config';

        // Billing Profile
        this.BILLING_PROFILE = '/api/billing/profile';
        this.PROFILE_GET_BY_ORG_ID = '/{orgId}';
        this.PROFILE_PAYMENT_METHODS = '/{orgId}/payment-methods';
        this.PROFILE_PAYMENT_METHOD_BY_ID = '/{orgId}/payment-methods/{methodId}';
        this.PROFILE_SET_DEFAULT_PAYMENT_METHOD = '/{orgId}/payment-methods/{methodId}/set-default';
    }

    handleSessionError(data) {
        if (!data || typeof data !== 'object') return data;

        if (data.error !== undefined) {
            if (data.error === 'Session Expired') {
                let userAuthService = new UserAuthService();
                userAuthService.logout();
            } else {
                localStorage.setItem('errorUUID', data.uuid);
            }
        }

        const normalizedMessage = typeof data.message === 'string'
            ? data.message.trim().toLowerCase()
            : '';

        const hasBody = data.body !== undefined && data.body !== null;
        data.hasBody = hasBody;
        data.isSuccess = data.error === undefined && (hasBody || normalizedMessage === 'success');

        return data;
    }

    // ─── Config ──────────────────────────────────────────────────────────────

    async fetchBillingConfig() {
        try {
            const url = window.location.origin + this.BILLING_CONFIG;
            return this.fetch(url, { method: 'GET', retries: 3, retryDelay: 200 })
                .then(res => res.json())
                .then(data => {
                    this.handleSessionError(data);
                    if (data?.body) return JSON.parse(data.body);
                    return data;
                });
        } catch (err) {
            console.log(err);
        }
    }

    // ─── Plans ───────────────────────────────────────────────────────────────

    async getAllPlans() {
        try {
            let url = window.location.origin + this.BILLING_PLAN;
            const data = await this.fetch(url, {
                method: 'GET',
                retries: 10,
                retryDelay: 200
            }).then(data => data.json())
                .then(data => this.handleSessionError(data));
            return data;
        } catch (err) {
            console.log(err);
        }
    }

    async getPlanById(planId) {
        try {
            let url = window.location.origin + this.BILLING_PLAN + this.PLAN_GET_BY_ID;
            url = url.replace('{planId}', planId);
            const data = await this.fetch(url, {
                method: 'GET',
                retries: 10,
                retryDelay: 200
            }).then(data => data.json())
                .then(data => this.handleSessionError(data));
            return data;
        } catch (err) {
            console.log(err);
        }
    }

    async comparePlans() {
        try {
            let url = window.location.origin + this.BILLING_PLAN + this.PLAN_COMPARE;
            const data = await this.fetch(url, {
                method: 'GET',
                retries: 10,
                retryDelay: 200
            }).then(data => data.json())
                .then(data => this.handleSessionError(data));
            return data;
        } catch (err) {
            console.log(err);
        }
    }

    // ─── Subscriptions ───────────────────────────────────────────────────────

    async createSubscription(subscriptionMap) {
        let csrfToken = Cookies.get('XSRF-TOKEN');
        let url = window.location.origin + this.BILLING_SUBSCRIPTION;
        try {
            return this.fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-XSRF-TOKEN': csrfToken
                },
                body: JSON.stringify(subscriptionMap)
            }).then(data => data.json())
                .then(data => this.handleSessionError(data));
        } catch (err) {
            console.log(err);
        }
    }

    async getSubscriptionByOrgId(orgId) {
        try {
            let url = window.location.origin + this.BILLING_SUBSCRIPTION + this.SUBSCRIPTION_GET_BY_ORG_ID;
            url = url.replace('{orgId}', orgId);
            const data = await this.fetch(url, {
                method: 'GET',
                retries: 10,
                retryDelay: 200
            }).then(data => data.json())
                .then(data => this.handleSessionError(data));
            return data;
        } catch (err) {
            console.log(err);
        }
    }

    async upgradeSubscription(orgId, upgradeMap) {
        let csrfToken = Cookies.get('XSRF-TOKEN');
        let url = window.location.origin + this.BILLING_SUBSCRIPTION + this.SUBSCRIPTION_UPGRADE;
        url = url.replace('{orgId}', orgId);
        try {
            return this.fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-XSRF-TOKEN': csrfToken
                },
                body: JSON.stringify(upgradeMap)
            }).then(data => data.json())
                .then(data => this.handleSessionError(data));
        } catch (err) {
            console.log(err);
        }
    }

    async cancelSubscription(orgId, cancelMap) {
        let csrfToken = Cookies.get('XSRF-TOKEN');
        let url = window.location.origin + this.BILLING_SUBSCRIPTION + this.SUBSCRIPTION_CANCEL;
        url = url.replace('{orgId}', orgId);
        try {
            return this.fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-XSRF-TOKEN': csrfToken
                },
                body: JSON.stringify(cancelMap || {})
            }).then(data => data.json())
                .then(data => this.handleSessionError(data));
        } catch (err) {
            console.log(err);
        }
    }

    async reactivateSubscription(orgId) {
        let csrfToken = Cookies.get('XSRF-TOKEN');
        let url = window.location.origin + this.BILLING_SUBSCRIPTION + this.SUBSCRIPTION_REACTIVATE;
        url = url.replace('{orgId}', orgId);
        try {
            return this.fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-XSRF-TOKEN': csrfToken
                },
                body: JSON.stringify({})
            }).then(data => data.json())
                .then(data => this.handleSessionError(data));
        } catch (err) {
            console.log(err);
        }
    }

    async restartSubscription(orgId) {
        let csrfToken = Cookies.get('XSRF-TOKEN');
        let url = window.location.origin + this.BILLING_SUBSCRIPTION + this.SUBSCRIPTION_RESTART;
        url = url.replace('{orgId}', orgId);
        try {
            return this.fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-XSRF-TOKEN': csrfToken
                },
                body: JSON.stringify({})
            }).then(data => data.json())
                .then(data => this.handleSessionError(data));
        } catch (err) {
            console.log(err);
        }
    }

    async scheduleDowngrade(orgId, downgradeMap) {
        let csrfToken = Cookies.get('XSRF-TOKEN');
        let url = window.location.origin + this.BILLING_SUBSCRIPTION + this.SUBSCRIPTION_SCHEDULE_DOWNGRADE;
        url = url.replace('{orgId}', orgId);
        try {
            return this.fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-XSRF-TOKEN': csrfToken
                },
                body: JSON.stringify(downgradeMap)
            }).then(data => data.json())
                .then(data => this.handleSessionError(data));
        } catch (err) {
            console.log(err);
        }
    }

    async getProrationPreview(orgId, newPlanId, couponCode) {
        try {
            let url = window.location.origin + this.BILLING_SUBSCRIPTION + this.SUBSCRIPTION_PRORATION_PREVIEW;
            url = url.replace('{orgId}', orgId) + '?newPlanId=' + newPlanId;
            if (couponCode) url += '&couponCode=' + encodeURIComponent(couponCode);
            const data = await this.fetch(url, {
                method: 'GET',
                retries: 10,
                retryDelay: 200
            }).then(data => data.json())
                .then(data => this.handleSessionError(data));
            return data;
        } catch (err) {
            console.log(err);
        }
    }

    async createUpgradeOrder(orgId, newPlanId, couponCode) {
        let csrfToken = Cookies.get('XSRF-TOKEN');
        let url = window.location.origin + this.BILLING_SUBSCRIPTION + this.SUBSCRIPTION_CREATE_UPGRADE_ORDER;
        url = url.replace('{orgId}', orgId);
        try {
            return this.fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-XSRF-TOKEN': csrfToken },
                body: JSON.stringify({ newPlanId, couponCode: couponCode || null })
            }).then(data => data.json())
                .then(data => this.handleSessionError(data));
        } catch (err) {
            console.log(err);
        }
    }

    async confirmUpgrade(orgId, addonOrderId, razorpayOrderId, razorpayPaymentId, newPlanId, couponCode) {
        let csrfToken = Cookies.get('XSRF-TOKEN');
        let url = window.location.origin + this.BILLING_SUBSCRIPTION + this.SUBSCRIPTION_CONFIRM_UPGRADE;
        url = url.replace('{orgId}', orgId);
        try {
            return this.fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-XSRF-TOKEN': csrfToken },
                body: JSON.stringify({ addonOrderId, razorpayOrderId, razorpayPaymentId, newPlanId, couponCode: couponCode || null })
            }).then(data => data.json())
                .then(data => this.handleSessionError(data));
        } catch (err) {
            console.log(err);
        }
    }

    async confirmPayment(orgId, razorpayPaymentId, razorpaySubscriptionId) {
        let csrfToken = Cookies.get('XSRF-TOKEN');
        let url = window.location.origin + this.BILLING_SUBSCRIPTION + this.SUBSCRIPTION_CONFIRM_PAYMENT;
        url = url.replace('{orgId}', orgId);
        try {
            return this.fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-XSRF-TOKEN': csrfToken
                },
                body: JSON.stringify({ razorpayPaymentId, razorpaySubscriptionId })
            }).then(data => data.json())
                .then(data => this.handleSessionError(data));
        } catch (err) {
            console.log(err);
        }
    }

    async getSubscriptionHistory(orgId) {
        try {
            let url = window.location.origin + this.BILLING_SUBSCRIPTION + this.SUBSCRIPTION_HISTORY;
            url = url.replace('{orgId}', orgId);
            const data = await this.fetch(url, {
                method: 'GET',
                retries: 10,
                retryDelay: 200
            }).then(data => data.json())
                .then(data => this.handleSessionError(data));
            return data;
        } catch (err) {
            console.log(err);
        }
    }

    // ─── Usage ───────────────────────────────────────────────────────────────

    async recordTicket(orgId) {
        let csrfToken = Cookies.get('XSRF-TOKEN');
        let url = window.location.origin + this.BILLING_USAGE + this.USAGE_RECORD_TICKET;
        try {
            return this.fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-XSRF-TOKEN': csrfToken
                },
                body: JSON.stringify({ orgId })
            }).then(data => data.json())
                .then(data => this.handleSessionError(data));
        } catch (err) {
            console.log(err);
        }
    }

    async recordChatbotResolution(orgId) {
        let csrfToken = Cookies.get('XSRF-TOKEN');
        let url = window.location.origin + this.BILLING_USAGE + this.USAGE_RECORD_CHATBOT_RESOLUTION;
        try {
            return this.fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-XSRF-TOKEN': csrfToken
                },
                body: JSON.stringify({ orgId })
            }).then(data => data.json())
                .then(data => this.handleSessionError(data));
        } catch (err) {
            console.log(err);
        }
    }

    async getCurrentUsage(orgId) {
        try {
            let url = window.location.origin + this.BILLING_USAGE + this.USAGE_GET_CURRENT;
            url = url.replace('{orgId}', orgId);
            const data = await this.fetch(url, {
                method: 'GET',
                retries: 10,
                retryDelay: 200
            }).then(data => data.json())
                .then(data => this.handleSessionError(data));
            return data;
        } catch (err) {
            console.log(err);
        }
    }

    async getUsageHistory(orgId) {
        try {
            let url = window.location.origin + this.BILLING_USAGE + this.USAGE_GET_HISTORY;
            url = url.replace('{orgId}', orgId);
            const data = await this.fetch(url, {
                method: 'GET',
                retries: 10,
                retryDelay: 200
            }).then(data => data.json())
                .then(data => this.handleSessionError(data));
            return data;
        } catch (err) {
            console.log(err);
        }
    }

    // ─── Invoices ─────────────────────────────────────────────────────────────

    async getInvoicesByOrgId(orgId) {
        try {
            let url = window.location.origin + this.BILLING_INVOICE + this.INVOICE_GET_BY_ORG_ID;
            url = url.replace('{orgId}', orgId);
            const data = await this.fetch(url, {
                method: 'GET',
                retries: 10,
                retryDelay: 200
            }).then(data => data.json())
                .then(data => this.handleSessionError(data));
            return data;
        } catch (err) {
            console.log(err);
        }
    }

    async getInvoiceById(invoiceId) {
        try {
            let url = window.location.origin + this.BILLING_INVOICE + this.INVOICE_GET_BY_ID;
            url = url.replace('{invoiceId}', invoiceId);
            const data = await this.fetch(url, {
                method: 'GET',
                retries: 10,
                retryDelay: 200
            }).then(data => data.json())
                .then(data => this.handleSessionError(data));
            return data;
        } catch (err) {
            console.log(err);
        }
    }

    async ensureInvoiceForMonth(orgId, billingMonth) {
        let csrfToken = Cookies.get('XSRF-TOKEN');
        let url = window.location.origin + this.BILLING_INVOICE + this.INVOICE_ENSURE_FOR_MONTH;
        url = url.replace('{orgId}', orgId).replace('{billingMonth}', billingMonth);
        try {
            return this.fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-XSRF-TOKEN': csrfToken
                },
                body: JSON.stringify({})
            }).then(data => data.json())
                .then(data => this.handleSessionError(data));
        } catch (err) {
            console.log(err);
        }
    }

    getDownloadFilename(contentDisposition, fallbackFilename) {
        if (!contentDisposition) return fallbackFilename;

        const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
        if (utf8Match?.[1]) {
            return decodeURIComponent(utf8Match[1]);
        }

        const asciiMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
        if (asciiMatch?.[1]) {
            return asciiMatch[1];
        }

        return fallbackFilename;
    }

    isPdfBytes(bytes) {
        return bytes?.length >= 4
            && bytes[0] === 0x25
            && bytes[1] === 0x50
            && bytes[2] === 0x44
            && bytes[3] === 0x46;
    }

    base64ToBlob(base64, mimeType = 'application/pdf') {
        const normalizedBase64 = base64.replace(/^data:[^;]+;base64,/, '').replace(/\s/g, '');
        const binary = atob(normalizedBase64);
        const bytes = new Uint8Array(binary.length);

        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }

        return new Blob([bytes], { type: mimeType });
    }

    triggerBrowserDownload(blob, filename) {
        const objectUrl = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);

        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    }

    async downloadInvoice(invoiceId, invoiceNumber) {
        let url = window.location.origin + this.BILLING_INVOICE + this.INVOICE_DOWNLOAD;
        url = url.replace('{invoiceId}', invoiceId);
        try {
            const response = await this.fetch(url, { method: 'GET' });
            const fallbackFilename = invoiceNumber
                ? `${invoiceNumber.replace(/\//g, '-')}.pdf`
                : `invoice-${invoiceId}.pdf`;
            const headerFilename = this.getDownloadFilename(
                response.headers.get('content-disposition'),
                fallbackFilename
            );
            const contentType = response.headers.get('content-type') || '';
            const responseBuffer = await response.arrayBuffer();
            const responseBytes = new Uint8Array(responseBuffer);

            if (this.isPdfBytes(responseBytes)) {
                this.triggerBrowserDownload(new Blob([responseBuffer], { type: 'application/pdf' }), headerFilename);
                return;
            }

            const responseText = new TextDecoder().decode(responseBytes).trim();

            if (contentType.includes('application/json') || responseText.startsWith('{')) {
                const data = this.handleSessionError(JSON.parse(responseText));
                const payload = data?.body ? JSON.parse(data.body) : data;
                const base64 = payload?.data?.base64 ?? payload?.base64;
                const filename = payload?.data?.filename ?? payload?.filename ?? headerFilename;

                if (!base64) throw new Error('No PDF data in JSON response');

                this.triggerBrowserDownload(this.base64ToBlob(base64), filename);
                return;
            }

            if (!responseText) {
                throw new Error('Empty invoice download response');
            }

            this.triggerBrowserDownload(this.base64ToBlob(responseText), headerFilename);
        } catch (err) {
            console.log(err);
            throw err;
        }
    }

    // ─── Addons ──────────────────────────────────────────────────────────────

    async createAddonOrder(addonMap) {
        let csrfToken = Cookies.get('XSRF-TOKEN');
        let url = window.location.origin + this.BILLING_ADDON + this.ADDON_CREATE_ORDER;
        try {
            return this.fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-XSRF-TOKEN': csrfToken
                },
                body: JSON.stringify(addonMap)
            }).then(data => data.json())
                .then(data => this.handleSessionError(data));
        } catch (err) {
            console.log(err);
        }
    }

    async getAddonsByOrgId(orgId) {
        try {
            let url = window.location.origin + this.BILLING_ADDON + this.ADDON_GET_BY_ORG_ID;
            url = url.replace('{orgId}', orgId);
            const data = await this.fetch(url, {
                method: 'GET',
                retries: 10,
                retryDelay: 200
            }).then(data => data.json())
                .then(data => this.handleSessionError(data));
            return data;
        } catch (err) {
            console.log(err);
        }
    }

    // ─── Feature Gate ────────────────────────────────────────────────────────

    async checkFeature(orgId, feature) {
        try {
            let url = window.location.origin + this.BILLING_FEATURE_GATE + this.FEATURE_GATE_CHECK;
            url = url.replace('{orgId}', orgId);
            url = url + '?feature=' + feature;
            const data = await this.fetch(url, {
                method: 'GET',
                retries: 10,
                retryDelay: 200
            }).then(data => data.json())
                .then(data => this.handleSessionError(data));
            return data;
        } catch (err) {
            console.log(err);
        }
    }

    async getAllFeatures(orgId) {
        try {
            let url = window.location.origin + this.BILLING_FEATURE_GATE + this.FEATURE_GATE_ALL;
            url = url.replace('{orgId}', orgId);
            const data = await this.fetch(url, {
                method: 'GET',
                retries: 10,
                retryDelay: 200
            }).then(data => data.json())
                .then(data => this.handleSessionError(data));
            return data;
        } catch (err) {
            console.log(err);
        }
    }

    // ─── Coupons ─────────────────────────────────────────────────────────────

    async validateCoupon(code, planId) {
        try {
            let url = window.location.origin + this.BILLING_COUPON + this.COUPON_VALIDATE + '?code=' + encodeURIComponent(code);
            if (planId != null) url += '&planId=' + encodeURIComponent(planId);
            const data = await this.fetch(url, {
                method: 'GET',
                retries: 10,
                retryDelay: 200
            }).then(data => data.json())
                .then(data => this.handleSessionError(data));
            return data;
        } catch (err) {
            console.log(err);
        }
    }

    async resolvePlan(code, planId, billingCycle) {
        try {
            let url = window.location.origin + this.BILLING_COUPON + this.COUPON_RESOLVE_PLAN
                + '?code=' + encodeURIComponent(code)
                + '&planId=' + encodeURIComponent(planId)
                + '&billingCycle=' + encodeURIComponent(billingCycle || 'MONTHLY');
            const data = await this.fetch(url, {
                method: 'POST',
                headers: { 'X-XSRF-TOKEN': Cookies.get('XSRF-TOKEN') },
                retries: 3,
                retryDelay: 200
            }).then(data => data.json())
                .then(data => this.handleSessionError(data));
            return data;
        } catch (err) {
            console.log(err);
        }
    }

    async createCoupon(couponMap) {
        let csrfToken = Cookies.get('XSRF-TOKEN');
        let url = window.location.origin + this.BILLING_COUPON;
        try {
            return this.fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-XSRF-TOKEN': csrfToken
                },
                body: JSON.stringify(couponMap)
            }).then(data => data.json())
                .then(data => this.handleSessionError(data));
        } catch (err) {
            console.log(err);
        }
    }

    async listCoupons() {
        try {
            let url = window.location.origin + this.BILLING_COUPON;
            const data = await this.fetch(url, {
                method: 'GET',
                retries: 10,
                retryDelay: 200
            }).then(data => data.json())
                .then(data => this.handleSessionError(data));
            return data;
        } catch (err) {
            console.log(err);
        }
    }

    async deactivateCoupon(couponId) {
        let csrfToken = Cookies.get('XSRF-TOKEN');
        let url = window.location.origin + this.BILLING_COUPON + this.COUPON_DEACTIVATE;
        url = url.replace('{couponId}', couponId);
        try {
            return this.fetch(url, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'X-XSRF-TOKEN': csrfToken
                },
                body: JSON.stringify({})
            }).then(data => data.json())
                .then(data => this.handleSessionError(data));
        } catch (err) {
            console.log(err);
        }
    }

    // ─── Billing Profile ─────────────────────────────────────────────────────

    async upsertBillingProfile(profileMap) {
        let csrfToken = Cookies.get('XSRF-TOKEN');
        let url = window.location.origin + this.BILLING_PROFILE;
        try {
            return this.fetch(url, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'X-XSRF-TOKEN': csrfToken
                },
                body: JSON.stringify(profileMap)
            }).then(data => data.json())
                .then(data => this.handleSessionError(data));
        } catch (err) {
            console.log(err);
        }
    }

    async getBillingProfile(orgId) {
        try {
            let url = window.location.origin + this.BILLING_PROFILE + this.PROFILE_GET_BY_ORG_ID;
            url = url.replace('{orgId}', orgId);
            const data = await this.fetch(url, {
                method: 'GET',
                retries: 10,
                retryDelay: 200
            }).then(data => data.json())
                .then(data => this.handleSessionError(data));
            return data;
        } catch (err) {
            console.log(err);
        }
    }

    async addPaymentMethod(orgId, paymentMethodMap) {
        let csrfToken = Cookies.get('XSRF-TOKEN');
        let url = window.location.origin + this.BILLING_PROFILE + this.PROFILE_PAYMENT_METHODS;
        url = url.replace('{orgId}', orgId);
        try {
            return this.fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-XSRF-TOKEN': csrfToken
                },
                body: JSON.stringify(paymentMethodMap)
            }).then(data => data.json())
                .then(data => this.handleSessionError(data));
        } catch (err) {
            console.log(err);
        }
    }

    async getPaymentMethods(orgId) {
        try {
            let url = window.location.origin + this.BILLING_PROFILE + this.PROFILE_PAYMENT_METHODS;
            url = url.replace('{orgId}', orgId);
            const data = await this.fetch(url, {
                method: 'GET',
                retries: 10,
                retryDelay: 200
            }).then(data => data.json())
                .then(data => this.handleSessionError(data));
            return data;
        } catch (err) {
            console.log(err);
        }
    }

    async deletePaymentMethod(orgId, methodId) {
        let csrfToken = Cookies.get('XSRF-TOKEN');
        let url = window.location.origin + this.BILLING_PROFILE + this.PROFILE_PAYMENT_METHOD_BY_ID;
        url = url.replace('{orgId}', orgId).replace('{methodId}', methodId);
        try {
            return this.fetch(url, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'X-XSRF-TOKEN': csrfToken
                },
                body: JSON.stringify({})
            }).then(data => data.json())
                .then(data => this.handleSessionError(data));
        } catch (err) {
            console.log(err);
        }
    }

    async setDefaultPaymentMethod(orgId, methodId) {
        let csrfToken = Cookies.get('XSRF-TOKEN');
        let url = window.location.origin + this.BILLING_PROFILE + this.PROFILE_SET_DEFAULT_PAYMENT_METHOD;
        url = url.replace('{orgId}', orgId).replace('{methodId}', methodId);
        try {
            return this.fetch(url, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'X-XSRF-TOKEN': csrfToken
                },
                body: JSON.stringify({})
            }).then(data => data.json())
                .then(data => this.handleSessionError(data));
        } catch (err) {
            console.log(err);
        }
    }
}

export default BillingService;
